import { Box, TextField, Paper, List, ListItemButton, ListItemText, CircularProgress, Typography } from "@mui/material";
import { LoadScriptNext } from "@react-google-maps/api";
import { useEffect, useState } from "react";
import PlacesAutocomplete, { geocodeByAddress } from "react-places-autocomplete";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "../redux/features/userStateSlice";

const colors = {
  orange: {
    50: "#FFF8F0",
    100: "#FFE0B8",
    200: "#FFC266",
    base: "#FB8E00",
    600: "#E07D00",
    700: "#B86400",
  },
  gray: {
    50: "#F5F5F5",
    100: "#E0E0E0",
    200: "#C2C2C2",
    300: "#A3A3A3",
    400: "#737373",
    base: "#0C0C0C",
  },
  danger: {
    base: "#EF4444",
    200: "#FECACA",
    700: "#B91C1C",
  },
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    paddingX: "4px",
    paddingY: "4px",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    "& fieldset": {
      borderColor: colors.orange.base,
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: colors.orange[200],
    },
    "&.Mui-focused fieldset": {
      borderColor: colors.orange.base,
      boxShadow: `0 0 0 2px ${colors.orange[200]}33`,
    },
    "&.Mui-error fieldset": {
      borderColor: colors.danger.base,
    },
    "&.Mui-error.Mui-focused fieldset": {
      borderColor: colors.danger[700],
      boxShadow: `0 0 0 2px ${colors.danger[200]}55`,
    },
  },
  "& .MuiInputLabel-root": {
    color: colors.gray[400],
    fontWeight: 500,
    "&.Mui-focused": {
      color: colors.orange.base,
    },
    "&.Mui-error": {
      color: colors.danger.base,
    },
  },
  "& input": {
    padding: "12px 14px",
    fontSize: "1.0rem",
    color: colors.gray.base,
  },
};

export const MapPredictiveState = ({ onStateSelect = null, setChanges = () => {} }) => {
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const userState = useSelector((state) => state.userState);
  const dispatch = useDispatch();

  const handleSelect = async (address) => {
    try {
      setChanges(true);
      setLoading(true);
      const results = await geocodeByAddress(address);
      if (results.length > 0) {
        const addressComponent = results[0].address_components;
        const selectedState = 
          addressComponent.find((component) => component.types.includes("administrative_area_level_1"))?.long_name ?? "";
        const selectedCountry = 
          addressComponent.find((component) => component.types.includes("country"))?.long_name ?? "";
        
        onStateSelect(selectedState, selectedCountry);
        setState(selectedState);
        setCountry(selectedCountry);
      }
    } catch (error) {
      console.error("Error getting state data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setState(userState?.state || "");
  }, [userState?.state]);

  useEffect(() => {
    dispatch(updateUser({ state, country }));
  }, [state, country, dispatch]);

  return (
    <LoadScriptNext googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} libraries={["places"]}>
      <Box sx={{ width: "100%", position: "relative" }}>
        <PlacesAutocomplete
          value={state !== "" ? state : userState?.state || ""}
          onChange={(value) => setState(value)}
          onSelect={handleSelect}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps, loading: placesLoading }) => (
            <Box>
              <TextField
                {...getInputProps({
                  label: "State",
                  placeholder: "Search for a state",
                  variant: "outlined",
                  size: "small",
                  fullWidth: true,
                })}
                sx={inputSx}
              />

              {(placesLoading || loading) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "12px",
                    marginTop: "4px",
                  }}
                >
                  <CircularProgress size={20} sx={{ color: colors.orange.base }} />
                </Box>
              )}

              {suggestions.length > 0 && !placesLoading && !loading && (
                <Paper
                  elevation={3}
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    marginTop: "4px",
                    borderRadius: "8px",
                    border: `1px solid ${colors.orange[200]}`,
                    overflow: "hidden",
                  }}
                >
                  <List sx={{ padding: 0 }}>
                    {suggestions.map((suggestion, index) => (
                      <ListItemButton
                        key={index}
                        {...getSuggestionItemProps(suggestion)}
                        sx={{
                          padding: "12px 16px",
                          borderBottom: index !== suggestions.length - 1 ? `1px solid ${colors.gray[100]}` : "none",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: colors.orange[50],
                            paddingLeft: "20px",
                          },
                          "&.active": {
                            backgroundColor: colors.orange[100],
                          },
                        }}
                      >
                        <ListItemText
                          primary={suggestion.description}
                          sx={{
                            "& .MuiListItemText-primary": {
                              fontSize: "0.95rem",
                              fontWeight: 500,
                              color: colors.gray.base,
                            },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              )}

              {!placesLoading && !loading && state && suggestions.length === 0 && (
                <Paper
                  elevation={3}
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    marginTop: "4px",
                    borderRadius: "8px",
                    border: `1px solid ${colors.orange[200]}`,
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ color: colors.gray[400], fontSize: "0.9rem" }}>
                    No states found
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </PlacesAutocomplete>
      </Box>
    </LoadScriptNext>
  );
};